import { useContext } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { buildCruiseInclusionView, buildCruiseInclusionDetails } from 'lib/cruises/cruiseInclusionsUtils'
import GeoContext from 'contexts/geoContext'
import { getAllOffers } from 'selectors/offerSelectors'
import { shallowEqual } from 'react-redux'

interface Props {
  offerId?: string
  departureDetails: App.CruiseDepartureDetails | undefined
}

export function useCruiseOfferInclusions({
  offerId,
  departureDetails,
}: Props) {
  const geo = useContext(GeoContext)
  const {
    offer, // loaded in the cruise offer page
    offerSummary, // loaded in the offer list page
    luxPlusEnabled,
  } = useAppSelector((state) => ({
    offer: state.cruise.cruiseOffers[offerId!],
    offerSummary: getAllOffers(state)[`cruise:${offerId}`],
    luxPlusEnabled: isLuxPlusEnabled(state),
  }), shallowEqual)

  let standardInclusions: Array<Cruises.InclusionItemView> = []
  let luxPlusInclusions: Array<Cruises.InclusionItemView> = []

  if (!departureDetails?.id || !departureDetails.lowestOverallPriceDetails.rateCode || !departureDetails.lowestOverallPriceDetails.total) {
    return { standardInclusions, luxPlusInclusions }
  }

  const offerInclusions = offerSummary?.offerInclusions || offer?.offerInclusions || []

  const filteredInclusions = offerInclusions.filter(({ departureIds, rateCodes }) => (
    (!departureIds.length || departureIds.includes(departureDetails.id)) &&
    (!rateCodes.length || rateCodes.includes(departureDetails.lowestOverallPriceDetails.rateCode))
  ))

  // Even if cruises has more than one group of inclusions, we will only consider one group of each type
  const luxPlusInclusionGroup = filteredInclusions.find(({ type }) => type === 'LUX_PLUS')
  const standardInclusionGroup = filteredInclusions.find(({ type }) => type !== 'LUX_PLUS')

  // Get inclusion items from the groups
  luxPlusInclusions = luxPlusInclusionGroup ? buildCruiseInclusionView(luxPlusInclusionGroup, departureDetails.lowestOverallPriceDetails.total, geo.currentRegionCode) : []
  standardInclusions = standardInclusionGroup ? buildCruiseInclusionView(standardInclusionGroup, departureDetails.lowestOverallPriceDetails.total, geo.currentRegionCode) : []

  const inclusionDetails = buildCruiseInclusionDetails([...standardInclusions, ...luxPlusInclusions])

  return {
    inclusionDetails,
    standardInclusions,
    luxPlusInclusions: luxPlusEnabled ? luxPlusInclusions : [],
  }
}
